import type { DataProvider, Identifier } from "ra-core";

import type { Contact, Project, ProjectImportRow, ProjectNote } from "../types";
import {
  normalizeIdentity,
  normalizePhone,
  sanitizeProjectStorageText,
  normalizeText,
} from "./projectCsvImport";
import { ensureReferralMiningTasks } from "./referralTasks";

export type ProjectImportCommitResult = {
  imported: number;
  skipped: number;
  failed: Array<{ rowNumber: number; message: string }>;
};

const getImportedNotes = (
  row: ProjectImportRow,
  projectId: Identifier,
  salesId: Identifier,
  fallbackDate: string,
) => {
  const notes = [
    { field: "Codex Notes", source: "codex" },
    { field: "Human Note", source: "human" },
  ] as const;

  return notes.flatMap(({ field, source }) => {
    const text = sanitizeProjectStorageText(row.raw[field])
      .replace(/\r\n/g, "\n")
      .trim();
    if (!text) return [];
    return [
      {
        project_id: projectId,
        text,
        source,
        type: source,
        date: row.project.lead_date
          ? `${row.project.lead_date}T00:00:00.000Z`
          : fallbackDate,
        sales_id: salesId,
      },
    ];
  });
};

const createProjectNotes = async (
  dataProvider: DataProvider,
  row: ProjectImportRow,
  projectId: Identifier,
  salesId: Identifier,
  fallbackDate: string,
) => {
  const notes = getImportedNotes(row, projectId, salesId, fallbackDate);
  for (const note of notes) {
    await dataProvider.create<ProjectNote>("project_notes", { data: note });
  }
};

const ensureProjectNotes = async (
  dataProvider: DataProvider,
  row: ProjectImportRow,
  project: Project,
  salesId: Identifier,
  fallbackDate: string,
) => {
  const notes = getImportedNotes(row, project.id, salesId, fallbackDate);
  if (!notes.length) return;

  const existing = await dataProvider.getList<ProjectNote>("project_notes", {
    filter: { project_id: project.id },
    pagination: { page: 1, perPage: 100 },
    sort: { field: "date", order: "ASC" },
  });
  for (const note of notes) {
    const alreadyExists = existing.data.some(
      (candidate) =>
        candidate.text === note.text &&
        (candidate.source || candidate.type) === note.source,
    );
    if (!alreadyExists) {
      await dataProvider.create<ProjectNote>("project_notes", { data: note });
    }
  }
};

const getContactImportKeys = (contact: Partial<Contact>): string[] => {
  const keys = [
    ...(contact.email_jsonb ?? [])
      .map((entry) => {
        const email = normalizeText(entry?.email).toLowerCase();
        return email ? `email:${email}` : null;
      })
      .filter((key): key is string => key != null),
    ...(contact.phone_jsonb ?? [])
      .map((entry) => {
        const phone = normalizePhone(entry?.number);
        return phone ? `phone:${phone}` : null;
      })
      .filter((key): key is string => key != null),
  ];
  const name = normalizeIdentity(
    `${contact.first_name ?? ""} ${contact.last_name ?? ""}`,
  );
  const address = normalizeIdentity(contact.address);
  if (name && address) keys.push(`name-address:${name}:${address}`);
  return keys;
};

export const commitProjectImport = async ({
  dataProvider,
  rows,
  batchId,
  fileName,
  salesId,
}: {
  dataProvider: DataProvider;
  rows: ProjectImportRow[];
  batchId: string;
  fileName: string;
  salesId: Identifier;
}): Promise<ProjectImportCommitResult> => {
  const failed: Array<{ rowNumber: number; message: string }> = [];
  const contactIdsByKey = new Map<string, Identifier>();
  let existingContactIdsByKey: Map<string, Set<Identifier>> | null = null;
  let imported = 0;
  let skipped = 0;

  const existingBatch = await dataProvider.getList<Project>("projects", {
    filter: { import_batch_id: batchId },
    pagination: { page: 1, perPage: 10_000 },
    sort: { field: "source_row_number", order: "ASC" },
  });
  const existingProjectsByRowNumber = new Map<number, Project>();
  existingBatch.data.forEach((project) => {
    if (
      project.import_batch_id === batchId &&
      project.source_row_number != null
    ) {
      existingProjectsByRowNumber.set(project.source_row_number, project);
    }
  });

  for (const row of rows) {
    const existingProject = existingProjectsByRowNumber.get(row.rowNumber);
    if (existingProject) {
      skipped += 1;
      try {
        const now = new Date().toISOString();
        await ensureProjectNotes(
          dataProvider,
          row,
          existingProject,
          salesId,
          now,
        );
        if (existingProject.stage === "referral-mining") {
          await ensureReferralMiningTasks(dataProvider, existingProject);
        }
      } catch (error) {
        failed.push({
          rowNumber: row.rowNumber,
          message:
            error instanceof Error ? error.message : "Unknown import error",
        });
      }
      continue;
    }

    try {
      let contactId = row.contactId;
      if (
        contactId == null &&
        row.contactMatch === "new" &&
        row.contact &&
        row.contactKey
      ) {
        const contactKey = row.contactKey;
        contactId = contactIdsByKey.get(contactKey);
        if (contactId == null && !contactKey.startsWith("name-row:")) {
          if (existingContactIdsByKey == null) {
            existingContactIdsByKey = new Map<string, Set<Identifier>>();
            try {
              const existingContacts = await dataProvider.getList<Contact>(
                "contacts",
                {
                  pagination: { page: 1, perPage: 10_000 },
                  sort: { field: "id", order: "ASC" },
                },
              );
              existingContacts.data.forEach((contact) => {
                getContactImportKeys(contact).forEach((key) => {
                  const ids =
                    existingContactIdsByKey?.get(key) ?? new Set<Identifier>();
                  ids.add(contact.id);
                  existingContactIdsByKey?.set(key, ids);
                });
              });
            } catch {
              // The preview already performed matching. If a connector cannot
              // repeat that read during commit, continue with a new contact
              // rather than preventing the Project row from being imported.
            }
          }
          const matchingIds = existingContactIdsByKey?.get(contactKey);
          if (matchingIds?.size === 1) {
            contactId = [...matchingIds][0];
            contactIdsByKey.set(contactKey, contactId);
          }
        }
        if (contactId == null) {
          const contactResponse = await dataProvider.create<Contact>(
            "contacts",
            {
              data: {
                ...row.contact,
                company_id: null,
                email_jsonb: row.contact.email_jsonb ?? [],
                phone_jsonb: row.contact.phone_jsonb ?? [],
                tags: [],
                gender: "",
                title: "",
                background: "",
                has_newsletter: false,
                status: "cold",
                sales_id: salesId,
                first_seen: new Date().toISOString(),
                last_seen: new Date().toISOString(),
              },
            },
          );
          contactId = contactResponse.data.id;
          contactIdsByKey.set(contactKey, contactId);
        }
      }

      const now = new Date().toISOString();
      const projectData = {
        ...row.project,
        name: row.project.name ?? `Unnamed project ${row.rowNumber}`,
        sales_id: salesId,
        import_batch_id: batchId,
        source_file_name: fileName,
        source_row_number: row.rowNumber,
        company_id: row.companyId ?? null,
        primary_contact_id: contactId ?? null,
        contact_ids: contactId != null ? [contactId] : [],
        amount: row.project.contract_amount ?? row.project.amount ?? 0,
        created_at: row.project.lead_date
          ? `${row.project.lead_date}T00:00:00.000Z`
          : now,
        updated_at: now,
      };
      const projectResponse = await dataProvider.create<Project>("projects", {
        data: projectData,
      });
      imported += 1;

      await createProjectNotes(
        dataProvider,
        row,
        projectResponse.data.id,
        salesId,
        now,
      );
      if (row.project.stage === "referral-mining") {
        await ensureReferralMiningTasks(dataProvider, {
          ...projectResponse.data,
          stage: "referral-mining",
        });
      }
    } catch (error) {
      failed.push({
        rowNumber: row.rowNumber,
        message:
          error instanceof Error ? error.message : "Unknown import error",
      });
    }
  }

  return { imported, skipped, failed };
};
